import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Gavel, Clock, MapPin, Users, DollarSign, Plus, X, Send, Filter, AlertTriangle, CheckCircle, XCircle, Trash2, Edit3, Save, Ban, MessageSquare, ArrowLeft, ExternalLink } from '../icons';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useConfirm } from '../components/Modal';
import {
  getBidListings, getBidListing, createBidListing, updateBidListing, deleteBidListing,
  getMyBidListings, placeBid, updateBidStatus, withdrawBid, getMyBids,
  blockBidder, unblockBidder, sendBidInquiry, getAppSettings
} from '../services/api';

function BiddingPage() {
  const { user, loading: authLoading } = useAuth();
  const [confirmDialog, ConfirmDialog] = useConfirm();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [listings, setListings] = useState([]);
  const [myListings, setMyListings] = useState([]);
  const [myBids, setMyBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [biddingEnabled, setBiddingEnabled] = useState(false);
  const [vendorBiddingApproved, setVendorBiddingApproved] = useState(null);
  const [view, setView] = useState('browse'); // browse, my-listings, my-bids, detail
  const [selectedListing, setSelectedListing] = useState(null);
  const [selectedBids, setSelectedBids] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState({ title: '', description: '', service_type: '', location: '', max_people: '', min_bid_npr: '', max_bid_npr: '', end_date: '' });
  const [creating, setCreating] = useState(false);
  const [bidAmount, setBidAmount] = useState('');
  const [bidMessage, setBidMessage] = useState('');
  const [bidding, setBidding] = useState(false);
  const [inquiryMessage, setInquiryMessage] = useState('');
  const [sendingInquiry, setSendingInquiry] = useState(false);
  const [editingListing, setEditingListing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [actionLoading, setActionLoading] = useState(null);
  const [msg, setMsg] = useState(null);

  const isVendor = user?.role === 'vendor';
  const isCouple = user?.role === 'couple';
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (authLoading) return;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate, authLoading]);

  const loadData = async () => {
    try {
      const settingsRes = await getAppSettings();
      const enabled = settingsRes.data?.bidding_enabled === 'true';
      setBiddingEnabled(enabled);
      if (!enabled && !isAdmin) { setLoading(false); return; }

      const listingsRes = await getBidListings();
      setListings(listingsRes.data.listings || []);

      if (isVendor) {
        const myRes = await getMyBidListings();
        setMyListings(myRes.data.listings || []);
        setVendorBiddingApproved(myRes.data.bidding_approved || null);
      }
      if (isCouple) {
        const bidsRes = await getMyBids();
        setMyBids(bidsRes.data);
      }
    } catch (err) {
      console.error('Failed to load bidding data', err);
    } finally {
      setLoading(false);
    }
  };

  const showMsg = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 4000); };

  const viewListingDetail = async (id) => {
    try {
      const res = await getBidListing(id);
      setSelectedListing(res.data.listing);
      setSelectedBids(res.data.bids);
      setView('detail');
    } catch (err) {
      showMsg('error', 'Failed to load listing details');
    }
  };

  const handleCreateListing = async (e) => {
    e.preventDefault();
    if (!createForm.title || !createForm.end_date) { showMsg('error', 'Title and end date are required'); return; }
    setCreating(true);
    try {
      await createBidListing({
        ...createForm,
        max_people: createForm.max_people ? parseInt(createForm.max_people) : null,
        min_bid_npr: createForm.min_bid_npr ? parseFloat(createForm.min_bid_npr) : 0,
        max_bid_npr: createForm.max_bid_npr ? parseFloat(createForm.max_bid_npr) : null,
      });
      showMsg('success', 'Bid listing created!');
      setShowCreateForm(false);
      setCreateForm({ title: '', description: '', service_type: '', location: '', max_people: '', min_bid_npr: '', max_bid_npr: '', end_date: '' });
      await loadData();
    } catch (err) {
      showMsg('error', err.response?.data?.error || 'Failed to create listing');
    } finally { setCreating(false); }
  };

  const handlePlaceBid = async (listingId) => {
    if (!user) { navigate('/login'); return; }
    if (!bidAmount || bidAmount <= 0) { showMsg('error', 'Enter a valid bid amount'); return; }
    setBidding(true);
    try {
      await placeBid(listingId, { amount_npr: parseFloat(bidAmount), message: bidMessage });
      showMsg('success', 'Bid placed successfully!');
      setBidAmount('');
      setBidMessage('');
      await loadData();
      if (view === 'detail') await viewListingDetail(listingId);
    } catch (err) {
      showMsg('error', err.response?.data?.error || 'Failed to place bid');
    } finally { setBidding(false); }
  };

  const handleBidStatus = async (bidId, status, note) => {
    setActionLoading(bidId);
    try {
      await updateBidStatus(bidId, { status, vendor_note: note });
      showMsg('success', `Bid ${status}!`);
      if (selectedListing) await viewListingDetail(selectedListing.id);
      await loadData();
    } catch (err) {
      showMsg('error', err.response?.data?.error || 'Failed to update bid');
    } finally { setActionLoading(null); }
  };

  const handleWithdrawBid = async (bidId) => {
    const ok = await confirmDialog({ title: 'Withdraw Bid', message: 'Withdraw this bid?', danger: true, confirmText: 'Withdraw' });
    if (!ok) return;
    setActionLoading(bidId);
    try {
      await withdrawBid(bidId);
      showMsg('success', 'Bid withdrawn');
      await loadData();
    } catch (err) {
      showMsg('error', err.response?.data?.error || 'Failed to withdraw');
    } finally { setActionLoading(null); }
  };

  const handleDeleteListing = async (id) => {
    const ok = await confirmDialog({ title: 'Delete Listing', message: 'Delete this listing and all its bids?', danger: true, confirmText: 'Delete' });
    if (!ok) return;
    try {
      await deleteBidListing(id);
      showMsg('success', 'Listing deleted');
      if (view === 'detail') setView('my-listings');
      await loadData();
    } catch (err) {
      showMsg('error', 'Failed to delete listing');
    }
  };

  const handleToggleListing = async (id, currentActive) => {
    try {
      await updateBidListing(id, { is_active: !currentActive });
      showMsg('success', currentActive ? 'Listing paused' : 'Listing activated');
      await loadData();
    } catch (err) {
      showMsg('error', 'Failed to update listing');
    }
  };

  const handleBlockBidder = async (userId) => {
    const ok = await confirmDialog({ title: 'Block Bidder', message: 'Block this bidder from all your listings?', danger: true, confirmText: 'Block' });
    if (!ok) return;
    try {
      await blockBidder({ user_id: userId, reason: 'Blocked from bidding page' });
      showMsg('success', 'Bidder blocked');
      if (selectedListing) await viewListingDetail(selectedListing.id);
    } catch (err) {
      showMsg('error', 'Failed to block bidder');
    }
  };

  const handleSendInquiry = async (listingId) => {
    if (!user) { navigate('/login'); return; }
    if (!inquiryMessage.trim()) return;
    setSendingInquiry(true);
    try {
      await sendBidInquiry(listingId, { message: inquiryMessage.trim() });
      showMsg('success', 'Inquiry sent to vendor!');
      setInquiryMessage('');
    } catch (err) {
      showMsg('error', err.response?.data?.error || 'Failed to send inquiry');
    } finally { setSendingInquiry(false); }
  };

  if (authLoading || loading) return <div className="loading-page"><div className="spinner" /></div>;

  if (!biddingEnabled && !isAdmin) {
    return (
      <div className="bidding-page">
        <div className="container" style={{ textAlign: 'center', padding: '80px 24px' }}>
          <Gavel size={64} style={{ color: '#d1d5db', marginBottom: '16px' }} />
          <h2 style={{ color: '#6b7280' }}>{t('bidding.comingSoonTitle')}</h2>
          <p style={{ color: '#9ca3af' }}>{t('bidding.comingSoonDesc')}</p>
        </div>
      </div>
    );
  }

  const timeLeft = (endDate) => {
    const diff = new Date(endDate) - new Date();
    if (diff <= 0) return t('bidding.ended');
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    if (days > 0) return `${days}d ${hours}h left`;
    const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${mins}m left`;
  };

  const bidStatusColors = { pending: '#f59e0b', accepted: '#16a34a', denied: '#dc2626', withdrawn: '#6b7280' };

  return (
    <div className="bidding-page">
      <div className="container">
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#1f2937', margin: '0 0 4px' }}>
            <Gavel size={28} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
            {t('bidding.title')}
          </h1>
          <p style={{ color: '#6b7280', margin: 0 }}>
            {isCouple ? t('bidding.coupleDesc') : isVendor ? t('bidding.vendorDesc') : 'Manage the bidding marketplace.'}
          </p>
        </div>

        {msg && (
          <div className={`alert alert-${msg.type}`} style={{ marginBottom: '16px' }}>{msg.text}<button className="alert-close" onClick={() => setMsg(null)}>&times;</button></div>
        )}

        {/* Tab Navigation */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
          <button onClick={() => setView('browse')} style={{ ...tabStyle, ...(view === 'browse' ? activeTabStyle : {}) }}>
            {t('bidding.browseListings')} ({listings.length})
          </button>
          {isVendor && (
            <button onClick={() => setView('my-listings')} style={{ ...tabStyle, ...(view === 'my-listings' ? activeTabStyle : {}) }}>
              {t('bidding.myListings')} ({myListings.length})
            </button>
          )}
          {isCouple && (
            <button onClick={() => setView('my-bids')} style={{ ...tabStyle, ...(view === 'my-bids' ? activeTabStyle : {}) }}>
              {t('bidding.myBids')} ({myBids.length})
            </button>
          )}
        </div>

        {/* BROWSE LISTINGS */}
        {view === 'browse' && (
          <div>
            {listings.length === 0 ? (
              <div className="detail-card" style={{ textAlign: 'center', padding: '48px 24px' }}>
                <Gavel size={48} style={{ color: '#d1d5db', marginBottom: '12px' }} />
                <h3 style={{ color: '#6b7280' }}>{t('bidding.noActiveListings')}</h3>
                <p style={{ color: '#9ca3af', fontSize: '14px' }}>{t('bidding.checkBackSoon')}</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '16px' }}>
                {listings.map(listing => (
                  <div key={listing.id} className="detail-card" style={{ padding: '20px', cursor: 'pointer' }} onClick={() => viewListingDetail(listing.id)}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                      <h3 style={{ margin: 0, fontSize: '1rem', color: '#1f2937' }}>{listing.title}</h3>
                      <span style={{ fontSize: '12px', fontWeight: 600, padding: '3px 10px', borderRadius: '10px', background: '#FEF3C7', color: '#92400E', whiteSpace: 'nowrap' }}>
                        <Clock size={11} style={{ verticalAlign: 'middle' }} /> {timeLeft(listing.end_date)}
                      </span>
                    </div>
                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
                      <Link to={`/vendor/${listing.vendor_slug}`} onClick={e => e.stopPropagation()} style={{ color: '#DC143C', fontWeight: 600, textDecoration: 'none' }}>
                        {listing.business_name}
                      </Link>
                      {listing.is_verified && <CheckCircle size={12} style={{ color: '#16a34a', marginLeft: '4px', verticalAlign: 'middle' }} />}
                      <span> &middot; {listing.vendor_category} &middot; {listing.vendor_city}</span>
                    </div>
                    {listing.description && <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 10px', lineHeight: 1.5 }}>{listing.description.substring(0, 120)}{listing.description.length > 120 ? '...' : ''}</p>}
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', fontSize: '13px', color: '#374151' }}>
                      {listing.service_type && <span><strong>Service:</strong> {listing.service_type}</span>}
                      {listing.location && <span><MapPin size={12} style={{ verticalAlign: 'middle' }} /> {listing.location}</span>}
                      {listing.max_people && <span><Users size={12} style={{ verticalAlign: 'middle' }} /> Up to {listing.max_people}</span>}
                    </div>
                    <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '10px', borderTop: '1px solid #f3f4f6' }}>
                      <span style={{ fontSize: '13px', color: '#6b7280' }}>
                        {listing.bid_count || 0} {t('bidding.bids')}
                        {listing.highest_bid && <span> &middot; {t('bidding.highest')} <strong style={{ color: '#16a34a' }}>{t('common.NPR')} {Number(listing.highest_bid).toLocaleString()}</strong></span>}
                      </span>
                      <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                        {t('bidding.min')} {t('common.NPR')} {Number(listing.min_bid_npr || 0).toLocaleString()}
                        {listing.max_bid_npr && <span> / {t('bidding.max')} {t('common.NPR')} {Number(listing.max_bid_npr).toLocaleString()}</span>}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* MY LISTINGS (Vendor) */}
        {view === 'my-listings' && isVendor && (
          <div>
            {vendorBiddingApproved !== 'approved' ? (
              <div className="detail-card" style={{ textAlign: 'center', padding: '48px 24px' }}>
                <Gavel size={48} style={{ color: vendorBiddingApproved === 'pending' ? '#f59e0b' : vendorBiddingApproved === 'denied' ? '#dc2626' : '#d1d5db', marginBottom: '12px' }} />
                {vendorBiddingApproved === 'pending' ? (
                  <>
                    <h3 style={{ color: '#92400e' }}>{t('bidding.accessPending')}</h3>
                    <p style={{ color: '#6b7280', fontSize: '14px' }}>{t('bidding.pendingDesc')}</p>
                  </>
                ) : vendorBiddingApproved === 'denied' ? (
                  <>
                    <h3 style={{ color: '#dc2626' }}>{t('bidding.accessDenied')}</h3>
                    <p style={{ color: '#6b7280', fontSize: '14px' }}>{t('bidding.deniedDesc')}</p>
                  </>
                ) : (
                  <>
                    <h3 style={{ color: '#6b7280' }}>{t('bidding.accessRequired')}</h3>
                    <p style={{ color: '#9ca3af', fontSize: '14px' }}>{t('bidding.requiredDesc')}</p>
                    <p style={{ color: '#9ca3af', fontSize: '13px' }}>Go to your <Link to="/vendor/portal" style={{ color: '#DC143C', fontWeight: 600 }}>Vendor Portal</Link> &rarr; Bidding tab to request access.</p>
                  </>
                )}
              </div>
            ) : (
            <>
            <div style={{ marginBottom: '16px' }}>
              <button onClick={() => setShowCreateForm(!showCreateForm)} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Plus size={16} /> {t('bidding.createNewListing')}
              </button>
            </div>

            {showCreateForm && (
              <div className="detail-card" style={{ padding: '24px', marginBottom: '20px' }}>
                <h3 style={{ marginTop: 0 }}>{t('bidding.newBidListing')}</h3>
                <form onSubmit={handleCreateListing}>
                  <div className="form-row">
                    <div className="form-group"><label>{t('bidding.listingTitle')} *</label><input type="text" value={createForm.title} onChange={e => setCreateForm({ ...createForm, title: e.target.value })} placeholder="e.g. Wedding Photography Package" required /></div>
                    <div className="form-group"><label>{t('bidding.serviceType')}</label><input type="text" value={createForm.service_type} onChange={e => setCreateForm({ ...createForm, service_type: e.target.value })} placeholder="e.g. Photography, Catering" /></div>
                  </div>
                  <div className="form-group"><label>{t('bidding.description')}</label><textarea rows={3} value={createForm.description} onChange={e => setCreateForm({ ...createForm, description: e.target.value })} placeholder="Describe what's included in this service..." /></div>
                  <div className="form-row">
                    <div className="form-group"><label>{t('bidding.location')}</label><input type="text" value={createForm.location} onChange={e => setCreateForm({ ...createForm, location: e.target.value })} placeholder="e.g. Kathmandu" /></div>
                    <div className="form-group"><label>{t('bidding.maxPeople')}</label><input type="number" value={createForm.max_people} onChange={e => setCreateForm({ ...createForm, max_people: e.target.value })} placeholder="e.g. 500" /></div>
                  </div>
                  <div className="form-row">
                    <div className="form-group"><label>{t('bidding.minBid')}</label><input type="number" value={createForm.min_bid_npr} onChange={e => setCreateForm({ ...createForm, min_bid_npr: e.target.value })} placeholder="0" /></div>
                    <div className="form-group"><label>{t('bidding.maxBid')}</label><input type="number" value={createForm.max_bid_npr} onChange={e => setCreateForm({ ...createForm, max_bid_npr: e.target.value })} placeholder="No limit" /></div>
                    <div className="form-group"><label>{t('bidding.endDate')} *</label><input type="datetime-local" value={createForm.end_date} onChange={e => setCreateForm({ ...createForm, end_date: e.target.value })} required /></div>
                  </div>
                  <div className="form-actions">
                    <button type="submit" className="btn btn-primary" disabled={creating}>{creating ? t('bidding.creating') : t('bidding.createListing')}</button>
                    <button type="button" className="btn btn-outline" onClick={() => setShowCreateForm(false)}>{t('common.cancel')}</button>
                  </div>
                </form>
              </div>
            )}

            {myListings.length === 0 ? (
              <div className="detail-card" style={{ textAlign: 'center', padding: '48px 24px' }}>
                <Gavel size={48} style={{ color: '#d1d5db', marginBottom: '12px' }} />
                <h3 style={{ color: '#6b7280' }}>{t('bidding.noListingsYet')}</h3>
                <p style={{ color: '#9ca3af', fontSize: '14px' }}>{t('bidding.createFirstListing')}</p>
              </div>
            ) : (
              myListings.map(listing => (
                <div key={listing.id} className="detail-card" style={{ padding: '20px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '1rem' }}>{listing.title}</h3>
                      <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                        {listing.bid_count || 0} {t('bidding.bids')} &middot; {timeLeft(listing.end_date)}
                        {listing.highest_bid && <span> &middot; {t('bidding.highest')} {t('common.NPR')} {Number(listing.highest_bid).toLocaleString()}</span>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 600, padding: '3px 10px', borderRadius: '10px', background: listing.is_active ? '#DCFCE7' : '#FEE2E2', color: listing.is_active ? '#16a34a' : '#dc2626' }}>
                        {listing.is_active ? 'Active' : 'Paused'}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button className="btn btn-outline" onClick={() => viewListingDetail(listing.id)} style={{ fontSize: '13px', padding: '6px 12px' }}>{t('bidding.viewBids')}</button>
                    <button className="btn btn-outline" onClick={() => handleToggleListing(listing.id, listing.is_active)} style={{ fontSize: '13px', padding: '6px 12px' }}>
                      {listing.is_active ? t('bidding.pause') : t('bidding.activate')}
                    </button>
                    <button className="btn btn-outline" onClick={() => handleDeleteListing(listing.id)} style={{ fontSize: '13px', padding: '6px 12px', color: '#dc2626', borderColor: '#fecaca' }}>
                      <Trash2 size={12} /> {t('common.delete')}
                    </button>
                  </div>
                </div>
              ))
            )}
            </>
            )}
          </div>
        )}

        {/* MY BIDS (Couple) */}
        {view === 'my-bids' && isCouple && (
          <div>
            {myBids.length === 0 ? (
              <div className="detail-card" style={{ textAlign: 'center', padding: '48px 24px' }}>
                <Gavel size={48} style={{ color: '#d1d5db', marginBottom: '12px' }} />
                <h3 style={{ color: '#6b7280' }}>{t('bidding.noBidsYet')}</h3>
                <p style={{ color: '#9ca3af', fontSize: '14px' }}>{t('bidding.browseBidsDesc')}</p>
              </div>
            ) : (
              myBids.map(bid => (
                <div key={bid.id} className="detail-card" style={{ padding: '20px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '1rem' }}>{bid.listing_title}</h3>
                      <Link to={`/vendor/${bid.vendor_slug}`} style={{ fontSize: '13px', color: '#DC143C', textDecoration: 'none' }}>
                        {bid.business_name} &middot; {bid.vendor_city}
                      </Link>
                    </div>
                    <span style={{ fontSize: '12px', fontWeight: 600, padding: '4px 12px', borderRadius: '12px', background: (bidStatusColors[bid.status] || '#6b7280') + '18', color: bidStatusColors[bid.status], textTransform: 'capitalize' }}>
                      {bid.status}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '16px', fontSize: '14px', color: '#374151', marginBottom: '8px' }}>
                    <span><strong>{t('bidding.yourBid')}</strong> {t('common.NPR')} {Number(bid.amount_npr).toLocaleString()}</span>
                    <span style={{ color: '#6b7280' }}>{new Date(bid.created_at).toLocaleDateString()}</span>
                  </div>
                  {bid.message && <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 8px' }}>{bid.message}</p>}
                  {bid.vendor_note && (
                    <div style={{ background: '#EFF6FF', padding: '10px 14px', borderRadius: '8px', border: '1px solid #BFDBFE', fontSize: '13px', marginBottom: '8px' }}>
                      <strong style={{ color: '#1D4ED8' }}>{t('bidding.vendorNote')}</strong> {bid.vendor_note}
                    </div>
                  )}
                  {bid.status === 'pending' && (
                    <button className="btn btn-outline" onClick={() => handleWithdrawBid(bid.id)} disabled={actionLoading === bid.id} style={{ fontSize: '13px', padding: '6px 12px', color: '#dc2626', borderColor: '#fecaca' }}>
                      {t('bidding.withdrawBid')}
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* DETAIL VIEW */}
        {view === 'detail' && selectedListing && (
          <div>
            <button onClick={() => setView(isVendor ? 'my-listings' : 'browse')} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', marginBottom: '16px', padding: 0 }}>
              <ArrowLeft size={16} /> Back
            </button>

            <div className="detail-card" style={{ padding: '24px', marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <h2 style={{ margin: '0 0 4px' }}>{selectedListing.title}</h2>
                  <Link to={`/vendor/${selectedListing.vendor_slug}`} style={{ color: '#DC143C', fontWeight: 600, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    {selectedListing.business_name} {selectedListing.is_verified && <CheckCircle size={14} style={{ color: '#16a34a' }} />} <ExternalLink size={12} />
                  </Link>
                </div>
                <span style={{ fontSize: '13px', fontWeight: 600, padding: '6px 14px', borderRadius: '10px', background: '#FEF3C7', color: '#92400E' }}>
                  <Clock size={13} style={{ verticalAlign: 'middle' }} /> {timeLeft(selectedListing.end_date)}
                </span>
              </div>
              {selectedListing.description && <p style={{ color: '#374151', lineHeight: 1.6, marginBottom: '12px' }}>{selectedListing.description}</p>}
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
                {selectedListing.service_type && <span><strong>Service:</strong> {selectedListing.service_type}</span>}
                {selectedListing.location && <span><MapPin size={12} style={{ verticalAlign: 'middle' }} /> {selectedListing.location}</span>}
                {selectedListing.max_people && <span><Users size={12} style={{ verticalAlign: 'middle' }} /> Up to {selectedListing.max_people} people</span>}
                <span><strong>{t('bidding.minBid')}:</strong> {t('common.NPR')} {Number(selectedListing.min_bid_npr || 0).toLocaleString()}</span>
                {selectedListing.max_bid_npr && <span><strong>{t('bidding.maxBid')}:</strong> {t('common.NPR')} {Number(selectedListing.max_bid_npr).toLocaleString()}</span>}
                <span><strong>Ends:</strong> {new Date(selectedListing.end_date).toLocaleString()}</span>
              </div>

              {/* Couple: Place a Bid */}
              {selectedListing.is_active && new Date(selectedListing.end_date) > new Date() && (
                <div style={{ background: '#f9fafb', padding: '16px', borderRadius: '10px', border: '1px solid #e5e7eb', marginBottom: '16px' }}>
                  <h4 style={{ margin: '0 0 10px', fontSize: '14px' }}>{t('bidding.placeYourBid')}</h4>
                  {!user ? (
                    <div style={{ textAlign: 'center', padding: '8px 0' }}>
                      <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '10px' }}>{t('bidding.loginToBid')}</p>
                      <Link to="/login" className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        {t('bidding.loginLink')}
                      </Link>
                    </div>
                  ) : isCouple ? (
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <input type="number" value={bidAmount} onChange={e => setBidAmount(e.target.value)} placeholder={t('bidding.amount')} style={{ flex: '0 0 180px', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
                    <input type="text" value={bidMessage} onChange={e => setBidMessage(e.target.value)} placeholder={t('bidding.optionalMessage')} style={{ flex: '1', minWidth: '200px', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
                    <button className="btn btn-primary" onClick={() => handlePlaceBid(selectedListing.id)} disabled={bidding} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Gavel size={14} /> {bidding ? t('bidding.placing') : t('bidding.placeBid')}
                    </button>
                  </div>
                  ) : (
                    <p style={{ color: '#6b7280', fontSize: '13px', margin: 0 }}>{t('bidding.onlyCouples')}</p>
                  )}
                </div>
              )}

              {/* Send Inquiry */}
              {(isCouple || !user) && (
                <div style={{ background: '#EFF6FF', padding: '16px', borderRadius: '10px', border: '1px solid #BFDBFE' }}>
                  <h4 style={{ margin: '0 0 10px', fontSize: '14px', color: '#1D4ED8' }}>{t('bidding.sendInquiryToVendor')}</h4>
                  {!user ? (
                    <div style={{ textAlign: 'center', padding: '8px 0' }}>
                      <Link to="/login" className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        {t('bidding.loginToInquiry')}
                      </Link>
                    </div>
                  ) : (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input type="text" value={inquiryMessage} onChange={e => setInquiryMessage(e.target.value)} placeholder={t('bidding.askQuestion')} style={{ flex: '1', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
                    <button className="btn btn-primary" onClick={() => handleSendInquiry(selectedListing.id)} disabled={sendingInquiry || !inquiryMessage.trim()} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Send size={14} /> {sendingInquiry ? 'Sending...' : t('common.send')}
                    </button>
                  </div>
                  )}
                </div>
              )}
            </div>

            {/* Bids List */}
            <div className="detail-card" style={{ padding: '24px' }}>
              <h3 style={{ margin: '0 0 16px' }}>{t('bidding.bidsHeading')} ({selectedBids.length})</h3>
              {selectedBids.length === 0 ? (
                <p style={{ color: '#9ca3af', fontSize: '14px' }}>{t('bidding.noBidsPlaced')}</p>
              ) : (
                selectedBids.map(bid => (
                  <div key={bid.id} style={{ padding: '14px', border: '1px solid #e5e7eb', borderRadius: '10px', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px', flexWrap: 'wrap', gap: '8px' }}>
                      <div>
                        <strong>{bid.first_name} {bid.last_name}</strong>
                        <span style={{ fontSize: '13px', color: '#6b7280', marginLeft: '8px' }}>{new Date(bid.created_at).toLocaleDateString()}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontWeight: 700, color: '#16a34a', fontSize: '16px' }}>{t('common.NPR')} {Number(bid.amount_npr).toLocaleString()}</span>
                        <span style={{ fontSize: '12px', fontWeight: 600, padding: '3px 10px', borderRadius: '10px', background: (bidStatusColors[bid.status]) + '18', color: bidStatusColors[bid.status], textTransform: 'capitalize' }}>
                          {bid.status}
                        </span>
                      </div>
                    </div>
                    {bid.message && <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 8px' }}>{bid.message}</p>}
                    {bid.vendor_note && <div style={{ fontSize: '13px', color: '#1D4ED8', background: '#EFF6FF', padding: '8px 12px', borderRadius: '6px', margin: '0 0 8px' }}><strong>{t('bidding.yourNote')}</strong> {bid.vendor_note}</div>}
                    
                    {/* Vendor Actions */}
                    {isVendor && bid.status === 'pending' && (
                      <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
                        <button className="btn btn-outline" onClick={() => handleBidStatus(bid.id, 'accepted')} disabled={actionLoading === bid.id} style={{ fontSize: '12px', padding: '5px 12px', color: '#16a34a', borderColor: '#BBF7D0' }}>
                          <CheckCircle size={12} /> {t('bidding.accept')}
                        </button>
                        <button className="btn btn-outline" onClick={() => handleBidStatus(bid.id, 'denied')} disabled={actionLoading === bid.id} style={{ fontSize: '12px', padding: '5px 12px', color: '#dc2626', borderColor: '#fecaca' }}>
                          <XCircle size={12} /> {t('bidding.deny')}
                        </button>
                        <button className="btn btn-outline" onClick={() => handleBlockBidder(bid.user_id)} style={{ fontSize: '12px', padding: '5px 12px', color: '#6b7280' }}>
                          <Ban size={12} /> {t('bidding.blockBidder')}
                        </button>
                      </div>
                    )}
                    {/* Couple: withdraw own bid */}
                    {isCouple && bid.user_id === user.id && bid.status === 'pending' && (
                      <button className="btn btn-outline" onClick={() => handleWithdrawBid(bid.id)} disabled={actionLoading === bid.id} style={{ fontSize: '12px', padding: '5px 12px', color: '#dc2626', borderColor: '#fecaca', marginTop: '8px' }}>
                        {t('bidding.withdraw')}
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
      {ConfirmDialog}
    </div>
  );
}

const tabStyle = {
  padding: '10px 20px', border: '1px solid #d1d5db', background: '#fff', color: '#374151',
  borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
};
const activeTabStyle = {
  borderColor: '#DC143C', background: '#FEF2F2', color: '#DC143C',
};

export default BiddingPage;
